import React, { useState, useMemo } from 'react';
import { Activity } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateLBM } from '../../lib/calculatorUtils';

const LeanBodyMassCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [weightKg, setWeightKg] = useState('');
  const [bf, setBF] = useState('');

  const result = useMemo(() => {
    const w = parseFloat(weightKg);
    const b = parseFloat(bf);
    if (!w || !b || w < 20 || b <= 0 || b >= 100) return null;
    const lbm = calculateLBM(w, b);
    return { lbm, fatMass: +(w * b / 100).toFixed(1) };
  }, [weightKg, bf]);

  const inputs = (
    <div className="space-y-5" data-testid="lbm-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Composition</h2>
      <p className="text-slate-400 text-sm mb-5">Enter weight and body fat percentage.</p>
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <NumberField id="lbm-bf" label="Body Fat %" value={bf} onChange={setBF} placeholder="e.g. 22" suffix="%" min={2} max={70} testId="lbm-bf-input" required />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Lean Body Mass" value={result.lbm} unit="kg" accent="from-emerald-500 to-cyan-500" testId="lbm-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Total Weight" value={weightKg} unit="kg" testId="lbm-weight" />
        <StatRow label="Fat Mass" value={result.fatMass} unit="kg" color="text-rose-300" testId="lbm-fat-mass" />
        <StatRow label="Lean Body Mass" value={result.lbm} unit="kg" color="text-emerald-300" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Lean Body Mass is useful for protein planning, muscle tracking and dosing decisions in clinical settings.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your weight and body fat % to see your Lean Body Mass." />
  );

  return (
    <CalculatorLayout
      slug="lean-body-mass" title="Lean Body Mass Calculator"
      subtitle="Calculate your body weight excluding fat, essential for protein planning and muscle tracking."
      metaDescription="LBM Calculator to calculate Lean Body Mass from weight and body fat %."
      icon={Activity}
      accentGradient="from-emerald-500 to-cyan-500" accentColor="text-emerald-400"
      badgeBg="bg-emerald-500/10" badgeBorder="border-emerald-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<><p><strong className="text-slate-300">Formula:</strong> LBM = Weight × (1 − Body Fat % / 100)</p></>}
    />
  );
};

export default LeanBodyMassCalculatorPage;
